/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import * as React from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalButton,
} from "baseui/modal";
import { Input } from "baseui/input";
import { LabelSmall } from "baseui/typography";
import { useDexterTheme } from "../DesignSystem/Theme/ThemeContext";

interface SaveAsNewModalProps {
  isOpen: boolean;
  currentName: string;
  onSave: (newName: string) => void;
  onCancel: () => void;
}

export const SaveAsNewModal: React.FC<SaveAsNewModalProps> = ({
  isOpen,
  currentName,
  onSave,
  onCancel,
}) => {
  const { currentTheme } = useDexterTheme();

  // Generate default name with (1) suffix
  const generateDefaultName = (baseName: string): string => {
    // Check if name already has a number suffix like " (1)"
    // Add onto it if there is already a duplicate
    const match = baseName.match(/^(.+?)\s*\((\d+)\)$/);
    if (match) {
      const nameWithoutNumber = match[1];
      const number = parseInt(match[2], 10);

      return `${nameWithoutNumber} (${number + 1})`;
    }
    return `${baseName} (1)`;
  };

  const [newName, setNewName] = React.useState(() =>
    generateDefaultName(currentName)
  );
  const [error, setError] = React.useState<string>("");

  // Reset when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setNewName(generateDefaultName(currentName));
      setError("");
    }
  }, [isOpen, currentName]);

  const handleSave = (): void => {
    const trimmedName = newName.trim();

    if (!trimmedName) {
      setError("Project name cannot be empty");
      return;
    }

    // We should create a sane length limit for their project title
    if (trimmedName.length > 100) {
      setError("Project name must be 100 characters or less");
      return;
    }

    onSave(trimmedName);
  };

  const handleKeyPress = (e: React.KeyboardEvent): void => {
    if (e.key === "Enter") {
      handleSave();
    }
  };

  return (
    <Modal
      onClose={onCancel}
      isOpen={isOpen}
      animate
      autoFocus
      closeable
      overrides={{
        Dialog: {
          style: {
            backgroundColor: currentTheme.colors.pageBackgroundColor,
          },
        },
      }}
    >
      <ModalHeader>Save Mapping As New</ModalHeader>
      <ModalBody>
        <LabelSmall color={currentTheme.colors.text} marginBottom="8px">
          Enter a name for the new mapping:
        </LabelSmall>
        <Input
          value={newName}
          onChange={(e) => {
            setNewName(e.currentTarget.value);
            setError("");
          }}
          onKeyPress={handleKeyPress}
          error={!!error}
          autoFocus
          overrides={{
            Root: {
              style: {
                backgroundColor: currentTheme.colors.pageBackgroundColor,
              },
            },
            Input: {
              style: {
                backgroundColor: currentTheme.colors.pageBackgroundColor,
                color: currentTheme.colors.text,
              },
            },
          }}
        />
        {error && (
          <LabelSmall color={currentTheme.colors.negative} marginTop="4px">
            {error}
          </LabelSmall>
        )}
      </ModalBody>
      <ModalFooter>
        <ModalButton kind="tertiary" onClick={onCancel}>
          Cancel
        </ModalButton>
        <ModalButton onClick={handleSave}>Save As New</ModalButton>
      </ModalFooter>
    </Modal>
  );
};
